import { asyncTimeout } from "@use/async-timeout";
import { useEffect, useRef, useState } from "react";

import { getUrlFromSrc } from "@apps/viewer/src/ts/components/utils/AudioPlayer/utils";

/**
 * begin playback of the specified url and return controls to render an audio
 * player interface.
 */
export function useAudio({
  src,
  onEnd,
}: {
  src: string;

  /**
   * triggered once playback has ended
   */
  onEnd?(): void;
}): {
  isPlaying: boolean;
  timeElapsedMs: number | undefined;
  durationMs: number | undefined;

  /**
   * resume playback of the given audio file
   */
  resume(): void;

  /**
   * pause playback of the given audio file
   */
  pause(): void;

  /**
   * seek the audio file to the given timestamp (in milliseconds)
   */
  seek(timeMs: number): void;
} {
  const audioRef = useRef<HTMLAudioElement | undefined>(undefined);
  const durationMsRef = useRef<number | undefined>(undefined);
  const [isPlaying, setPlaying] = useState(true);
  const [timeElapsedMs, setTimeElapsedMs] = useState<number | undefined>(
    undefined,
  );

  useEffect(
    () => {
      let isActiveSrc = true;
      let animationFrame: number;

      const audio = document.createElement("audio");

      // cache reference to the playback element, so we can reference it later on
      // in the control methods that are exposed publicly
      audioRef.current = audio;

      Promise.all([getUrlFromSrc(src), asyncTimeout(500)])
        .then(([url]) => {
          if (!isActiveSrc) {
            // bail out if the active audio src has been changed since we requested
            // the absolute url
            return;
          }

          if (!url) {
            alert("Der opstod en fejl ved afspilning, prøv igen.");

            return;
          }

          audio.src = url;

          // reset internal state so we reflect that a new file is currently being
          // played
          durationMsRef.current = undefined;
          setTimeElapsedMs(undefined);
          setPlaying(false);

          // register when the audio file can begin playback
          audio.oncanplay = () => {
            setTimeElapsedMs(0);
          };

          // subscribe to progress using an animation frame loop while the audio is
          // still playing
          const onProgress = (): void => {
            if (audio.duration) {
              durationMsRef.current = audio.duration * 1000;
            }

            if (audio.currentTime) {
              setTimeElapsedMs(audio.currentTime * 1000);
            }

            animationFrame = requestAnimationFrame(onProgress);
          };

          animationFrame = requestAnimationFrame(onProgress);
        })
        .catch((err) => {
          alert("Der opstod en fejl ved afspilning, prøv igen.");
          throw err;
        });

      return (): void => {
        // register that this src is no longer actively requested
        isActiveSrc = false;

        // stop playback when the component is unmounted
        audio.pause();

        // stop tracking progress once the component has been unmounted
        cancelAnimationFrame(animationFrame);
      };
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [src],
  );

  // bind event listeners to ensure that audio progress is going to be emitted
  // properly
  useEffect(() => {
    const audio = audioRef.current;

    if (audio) {
      audio.onended = (): void => {
        setPlaying(false);
        setTimeElapsedMs(0);

        audio.currentTime = 0;

        if (onEnd) {
          onEnd();
        }
      };
    }
    if (audioRef.current && onEnd) {
      audioRef.current.onended = onEnd;
    }
  },        [src, onEnd]);

  return {
    resume(): void {
      audioRef.current?.play();
      setPlaying(true);
    },

    pause(): void {
      audioRef.current?.pause();
      setPlaying(false);
    },

    seek(timeMs: number): void {
      if (audioRef.current) {
        audioRef.current.currentTime = timeMs;
      }
    },

    isPlaying,
    durationMs: durationMsRef.current,
    timeElapsedMs: timeElapsedMs,
  };
}
